import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import { WizardStepper } from './WizardStepper';

export interface ContractWizardStep {
  key: string;
  /** Label in the step indicator and in "Nästa: …". */
  title: string;
  subtitle?: string;
  /** Edit mode: the section has unsaved or unsent changes. */
  changed?: boolean;
  /** Show the summary column next to this step (desktop only). Default true. */
  showSummary?: boolean;
  /** `goTo` lets content jump back to an earlier step (e.g. "Ändra" in the preview). */
  render: (navigation: { goTo: (stepIndex: number) => void }) => React.ReactNode;
  /**
   * Runs when the user tries to leave the step forwards. Return false to stay;
   * the step itself is responsible for showing why (banner + field errors).
   */
  validate?: () => boolean | Promise<boolean>;
}

export interface ContractWizardFinalAction {
  label: string;
  onClick: () => void | Promise<void>;
  busy?: boolean;
  /** Why the action cannot be taken yet (e.g. no Beställare); disables it. */
  blockedReason?: string | null;
}

export interface ContractWizardModalProps {
  mode: 'create' | 'edit';
  title: string;
  subtitle?: string;
  steps: ContractWizardStep[];
  initialStep?: number;
  /** Right-hand summary column, rendered for steps with showSummary !== false. */
  summary?: React.ReactNode;
  /** Unsaved input exists; closing asks for confirmation first. */
  dirty: boolean;
  /** Save state text, e.g. "Utkast sparat kl. 14:32" (announced politely). */
  saveStatus?: string;
  onSave?: () => void | Promise<void>;
  saving?: boolean;
  /** Primary action on the last step (e.g. sign and send). */
  finalAction?: ContractWizardFinalAction;
  onClose: () => void;
  /**
   * Create mode: discards the draft already stored for this wizard. Given only
   * once a draft exists; cancelling then removes it unless the user chose
   * "Spara utkast", so an unsent draft never lingers (ClickUp 869fkhh2r).
   */
  onDiscard?: () => Promise<void>;
  onStepChange?: (stepIndex: number) => void;
  /**
   * Dialogs opened from a step (e.g. the document library). Rendered outside
   * the wizard dialog: a fixed-position modal inside `.modal-dialog` would be
   * clipped by its transform.
   */
  overlays?: React.ReactNode;
  /** Edit mode: the contract's state, shown above the step content. */
  banner?: React.ReactNode;
}

/**
 * Overlay wizard for creating and editing a contract. Owns navigation only:
 * which step is shown, which steps are reachable, the footer and the close
 * confirmation. Step content, validation and persistence come from the caller
 * through `steps`, so each step can be built and tested on its own.
 */
export function ContractWizardModal({
  mode,
  title,
  subtitle,
  steps,
  initialStep = 0,
  summary,
  dirty,
  saveStatus,
  onSave,
  saving = false,
  finalAction,
  onClose,
  onDiscard,
  onStepChange,
  overlays,
  banner,
}: ContractWizardModalProps) {
  const { t } = useTranslation();
  const lastIndex = steps.length - 1;
  const clampStep = (value: number) => Math.min(Math.max(value, 0), lastIndex);
  const [currentStep, setCurrentStep] = useState(() => clampStep(initialStep));
  // Edit mode treats steps as sections: every one is reachable from the start.
  const [maxVisited, setMaxVisited] = useState(() => (mode === 'edit' ? lastIndex : clampStep(initialStep)));
  const [validating, setValidating] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  // "Spara utkast" keeps the draft; otherwise cancelling discards it.
  const [keptDraft, setKeptDraft] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  // Only "Avbryt" discards; closing with the cross keeps the autosaved draft
  // to continue later (it stays hidden from the Beställare until sent).
  const [discardRequested, setDiscardRequested] = useState(false);
  const discardOnCancel = discardRequested && mode === 'create' && !!onDiscard && !keptDraft;

  const step = steps[currentStep];
  const nextStep = steps[currentStep + 1];

  // A new step starts at its top: the wizard scrolls back up (whatever scrolls
  // it – the modal body, or the whole dialog on a landscape phone) and focus
  // moves to the step's heading, so "Nästa" at the bottom of a long step does
  // not leave the user in the middle of the next one. "Tillbaka" instead
  // returns to where the user left the previous step.
  const mainRef = useRef<HTMLDivElement>(null);
  const previousStepRef = useRef(currentStep);
  const scrollPositionsRef = useRef<Record<number, number[]>>({});
  const restoreScrollRef = useRef(false);
  const scrollers = () =>
    Array.from(mainRef.current?.closest('.modal-dialog')?.querySelectorAll<HTMLElement>('.modal-body, .modal-content') ?? []);
  const rememberScroll = () => {
    scrollPositionsRef.current[currentStep] = scrollers().map(element => element.scrollTop);
  };
  useEffect(() => {
    if (previousStepRef.current === currentStep) return;
    previousStepRef.current = currentStep;
    const main = mainRef.current;
    if (!main) return;
    const saved = restoreScrollRef.current ? scrollPositionsRef.current[currentStep] : undefined;
    restoreScrollRef.current = false;
    const elements = scrollers();
    elements.forEach((element, index) => {
      element.scrollTop = saved?.[index] ?? 0;
    });
    if (saved) {
      // Content that lays itself out after mounting (the description editors)
      // can still change the height: set the position once more after paint.
      const frame = window.requestAnimationFrame(() => {
        elements.forEach((element, index) => { element.scrollTop = saved[index] ?? 0; });
      });
      return () => window.cancelAnimationFrame(frame);
    }
    const heading = main.querySelector<HTMLElement>('h2');
    if (heading) {
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);
  const showSummary = !!summary && step.showSummary !== false;
  // A single read-only section (a signed or previewed contract): only "Stäng",
  // plus a final action such as "Gå till signering" when one is given.
  const readOnly = !onSave && steps.length === 1;

  const goTo = (index: number) => {
    const target = clampStep(index);
    rememberScroll();
    setCurrentStep(target);
    setMaxVisited(previous => Math.max(previous, target));
    onStepChange?.(target);
  };

  const goBack = () => {
    restoreScrollRef.current = true;
    goTo(currentStep - 1);
  };

  // While a save or a validation runs, moving on would start another save of
  // the same data in parallel (ClickUp 869fh1kfj).
  const busy = validating || saving;

  const handleNext = async () => {
    if (!nextStep || busy) return;
    if (step.validate) {
      setValidating(true);
      try {
        const ok = await step.validate();
        if (!ok) return;
      } finally {
        setValidating(false);
      }
    }
    goTo(currentStep + 1);
  };

  const handleStepClick = async (index: number) => {
    if (busy) return;
    // Jumping forwards past the current step must still pass its validation.
    if (index > currentStep && step.validate) {
      setValidating(true);
      try {
        if (!(await step.validate())) return;
      } finally {
        setValidating(false);
      }
    }
    goTo(index);
  };

  const requestCancel = () => {
    if (mode === 'create' && onDiscard && !keptDraft) {
      setDiscardRequested(true);
      setConfirmClose(true);
      return;
    }
    requestClose();
  };

  const requestClose = () => {
    setDiscardRequested(false);
    if (dirty) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  const confirmTexts = mode === 'edit'
    ? {
        title: t('contracts.wizard.closeConfirm.editTitle'),
        body: t('contracts.wizard.closeConfirm.editBody'),
        confirm: t('contracts.wizard.closeConfirm.editConfirm'),
      }
    : {
        title: t('contracts.wizard.closeConfirm.createTitle'),
        body: t('contracts.wizard.closeConfirm.createBody'),
        confirm: t('contracts.wizard.closeConfirm.createConfirm'),
      };

  const footer = (
    <div className="contract-wizard-footer">
      <div className="contract-wizard-footer-secondary">
        {/* Read-only (signed contract): nothing to cancel, only close — on every screen size. */}
        {readOnly ? (
          <button type="button" className="btn contract-wizard-btn-outline flex-fill flex-md-grow-0" onClick={requestClose}>
            {t('common.close', 'Stäng')}
          </button>
        ) : (
          <button type="button" className="btn btn-link contract-wizard-link-muted d-none d-md-inline-block" onClick={requestCancel}>
            {t('contracts.wizard.footer.cancel')}
          </button>
        )}
        {onSave && (
          <button
            type="button"
            className="btn btn-link contract-wizard-link"
            onClick={() => {
              setKeptDraft(true);
              void onSave();
            }}
            disabled={saving}
          >
            <i className="feather-save me-2" aria-hidden="true"></i>
            {mode === 'edit' ? t('contracts.wizard.footer.saveChanges') : t('contracts.wizard.footer.saveDraft')}
          </button>
        )}
        <span className="contract-wizard-save-status" aria-live="polite">{saveStatus}</span>
      </div>
      <div className="contract-wizard-footer-primary">
        {currentStep > 0 && (
          <button type="button" className="btn contract-wizard-btn-outline" onClick={goBack}>
            <i className="feather-arrow-left me-2" aria-hidden="true"></i>
            {t('contracts.wizard.footer.back')}
          </button>
        )}
        {nextStep ? (
          <button
            type="button"
            className="btn contract-wizard-btn-navy flex-fill flex-md-grow-0"
            onClick={() => void handleNext()}
            disabled={busy}
          >
            <span className="d-none d-md-inline">{t('contracts.wizard.footer.next', { title: nextStep.title })}</span>
            <span className="d-md-none">{t('contracts.wizard.footer.nextShort')}</span>
            <i className="feather-arrow-right ms-2" aria-hidden="true"></i>
          </button>
        ) : (
          finalAction && (
            <>
              {finalAction.blockedReason && (
                <p className="contract-wizard-blocked-reason" id="contract-wizard-blocked-reason" data-testid="contract-wizard-blocked-reason">
                  <i className="feather-lock me-2" aria-hidden="true"></i>
                  {finalAction.blockedReason}
                </p>
              )}
              <button
                type="button"
                className="btn contract-wizard-btn-cta flex-fill flex-md-grow-0"
                onClick={() => void finalAction.onClick()}
                disabled={finalAction.busy || Boolean(finalAction.blockedReason)}
                aria-describedby={finalAction.blockedReason ? 'contract-wizard-blocked-reason' : undefined}
                data-testid="contract-wizard-final-action"
              >
                {finalAction.busy && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>}
                {finalAction.label}
              </button>
            </>
          )
        )}
      </div>
    </div>
  );

  return (
    <>
      <Modal
        title={title}
        onClose={requestClose}
        size="xl"
        fullscreenBelow="md"
        dialogClassName="contract-wizard-dialog"
        closeAriaLabel={t('contracts.wizard.closeAria')}
        subheader={
          <div className="contract-wizard-subheader">
            {subtitle && <p className="contract-wizard-subtitle">{subtitle}</p>}
            {/* A single section (the signed contract) has nothing to step between. */}
            {steps.length > 1 && (
              <WizardStepper
                steps={steps}
                currentStep={currentStep}
                maxReachableStep={maxVisited}
                mode={mode}
                onStepClick={index => void handleStepClick(index)}
              />
            )}
          </div>
        }
        footer={footer}
      >
        <div className={`contract-wizard-body${showSummary ? ' has-summary' : ''}`}>
          <div ref={mainRef} className="contract-wizard-main" data-testid={`contract-wizard-step-${step.key}`}>
            {banner}
            {step.render({ goTo })}
          </div>
          {showSummary && (
            <aside className="contract-wizard-summary d-none d-lg-block" aria-label={t('contracts.wizard.summary.title')}>
              <div className="contract-wizard-summary-inner">
                <h2 className="contract-wizard-kicker">{t('contracts.wizard.summary.title')}</h2>
                {summary}
              </div>
            </aside>
          )}
        </div>
      </Modal>

      {overlays}

      {confirmClose && (
        <Modal
          title={confirmTexts.title}
          onClose={() => setConfirmClose(false)}
          zIndex={1060}
          footer={
            <>
              <button type="button" className="btn contract-wizard-btn-outline" onClick={() => setConfirmClose(false)}>
                {t('contracts.wizard.closeConfirm.keepEditing')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={discarding}
                onClick={async () => {
                  if (discardOnCancel) {
                    setDiscarding(true);
                    try {
                      await onDiscard!();
                    } finally {
                      setDiscarding(false);
                    }
                  }
                  setConfirmClose(false);
                  onClose();
                }}
              >
                {confirmTexts.confirm}
              </button>
            </>
          }
        >
          <p className="mb-0">{confirmTexts.body}</p>
        </Modal>
      )}
    </>
  );
}

export default ContractWizardModal;
